// Appareils: every fitted kit, the ones to watch first (silent gateway, low
// battery, weak signal, old firmware), the stock at the depots and the
// installs that failed.
import { useEffect, useMemo, useState } from 'react';
import { S } from '../lib/strings';
import { dayLabel, hhmm, minutesBetween, rel } from '../lib/format';
import type { Source } from '../lib/api';
import type { DeviceKit, Devices as Dv } from '../lib/types';
import { MiniBottle } from '../components/Bottles';
import { Ic, type IconName } from '../components/Icon';
import { Bat, Sig, Strip } from '../components/Device';
import { StPill } from './Orders';
import { Loading } from '../components/Loading';

type DF = 'all' | 'watch' | 'off' | 'stock' | 'fail';
const LOW_BAT = 20;

/** The firmware most kits run: anything else is "old" until it is updated. */
function mainFw(kits: DeviceKit[]) {
  const n: Record<string, number> = {};
  kits.forEach((k) => { if (k.gateway.fw) n[k.gateway.fw] = (n[k.gateway.fw] ?? 0) + 1; });
  return Object.entries(n).sort((a, b) => b[1] - a[1] || b[0].localeCompare(a[0]))[0]?.[0] ?? null;
}
const lowBat = (k: DeviceKit) => (k.sensor.battery_pct ?? 100) < LOW_BAT || (k.gateway.battery_pct ?? 100) < LOW_BAT;
const watch = (k: DeviceKit, fw: string | null) => !k.online || lowBat(k) || (k.gateway.rssi ?? 0) < -90 || (!!fw && !!k.gateway.fw && k.gateway.fw !== fw);

interface Props { src: Source; refresh: Date | null; openHome(id: string): void; openOrder(id: string): void }

export function Devices({ src, refresh, openHome, openOrder }: Props) {
  const [d, setD] = useState<Dv | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [f, setF] = useState<DF>('all');
  useEffect(() => { src.devices().then((x) => { setD(x); setErr(null); }).catch((e) => setErr(String(e?.message ?? e))); }, [src, refresh]);
  const fw = useMemo(() => (d ? mainFw(d.kits) : null), [d]);

  if (err && !d) return <section className="view on"><div className="card empty" style={{ marginTop: 24 }}><b>{S.errors.load}</b><span className="mono">{err}</span></div></section>;
  if (!d) return <Loading />;
  const K = d.kits;
  const cnt: Record<DF, number> = { all: K.length, watch: K.filter((k) => watch(k, fw)).length, off: K.filter((k) => !k.online).length, stock: d.stock.length, fail: d.failed.length };
  const inDepot = d.stock.filter((s) => s.status === 'inventory');
  const kitsInStock = Math.min(inDepot.filter((s) => s.kind === 'uconnect').length, inDepot.filter((s) => s.kind === 'ultrasense').length);
  const kpis: [string, number, string, IconName, '' | 'g' | 'r' | 'a', string][] = [
    [S.dv.kpi.online, K.filter((k) => k.online).length, `/ ${K.length}`, 'wifi', 'g', S.dv.kpi.allIn],
    [S.dv.kpi.silent, cnt.off, '', 'alert', 'r', S.dv.kpi.toVisit],
    [S.dv.kpi.bat, K.filter(lowBat).length, '', 'battery', 'a', S.dv.kpi.under20],
    [S.dv.kpi.stock, kitsInStock, '', 'box', '', S.dv.kpi.total(d.stock.length)],
  ];
  const rows = K.filter((k) => f === 'all' || (f === 'watch' && watch(k, fw)) || (f === 'off' && !k.online))
    .sort((a, b) => Number(a.online) - Number(b.online) || Number(watch(b, fw)) - Number(watch(a, fw)) || a.name.localeCompare(b.name));

  return (
    <section className="view on" aria-label={S.dv.title}>
      <div className="ph"><div><h1>{S.dv.title}</h1><p>{S.dv.sub}</p></div></div>
      <div className="grid g4">
        {kpis.map(([t, n, s, i, c, foot]) => (
          <div className="card kpi" key={t}><div className="t"><span className={'ic ' + c}><Ic n={i} s="s" /></span>{t}</div>
            <div className="v"><span className="n num">{n}{s && <small>{s}</small>}</span></div><div className="f">{foot}</div></div>
        ))}
      </div>
      <div className="card" style={{ marginTop: 16 }}>
        <div className="fbar">
          {(Object.keys(S.dv.filters) as DF[]).map((k) => (
            <button key={k} className="fchip" aria-pressed={f === k} onClick={() => setF(k)}>{S.dv.filters[k]}<span className="c">{cnt[k]}</span></button>
          ))}
        </div>
        <div className="tblwrap">
          {f === 'stock' ? (
            d.stock.length ? <table className="tbl cards"><thead><tr>{S.dv.stockCols.map((c) => <th key={c}>{c}</th>)}</tr></thead><tbody>
              {d.stock.map((s) => <tr key={s.kind + s.serial}><td>{S.dv.kind[s.kind]}</td><td><b className="mono" style={{ color: 'var(--ink)' }}>{s.serial}</b></td><td>{s.agency}</td>
                <td><span className={'st ' + (s.status === 'inventory' ? 'green' : 'blue')}>{S.dv.status[s.status]}</span></td></tr>)}
            </tbody></table> : <div className="empty"><b>{S.dv.noStock}</b></div>
          ) : f === 'fail' ? (
            d.failed.length ? d.failed.map((o) => (
              <div key={o.id} style={{ padding: 20, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', borderTop: '1px solid var(--line)' }}>
                <div style={{ flex: 1, minWidth: 220 }}><b style={{ fontSize: 15 }}>{o.customer.name} · {o.customer.quartier}</b>
                  <div className="muted" style={{ fontSize: 13 }}>{dayLabel(o.events.failed_first_install ?? o.created_at)} {hhmm(o.events.failed_first_install ?? o.created_at)}{o.failure_note ? ' · ' + o.failure_note : ''}</div></div>
                <StPill o={o} />
                <button className="btn" onClick={() => openOrder(o.id)}>{S.dv.see}</button>
              </div>
            )) : <div className="empty"><b>{S.dv.noFail}</b>{S.dv.noFailSub}</div>
          ) : rows.length ? (
            <table className="tbl cards"><thead><tr>{S.dv.cols.map((c, i) => <th key={i}>{c}</th>)}</tr></thead><tbody>
              {rows.map((k) => (
                <tr key={k.customer_id} className={k.online ? '' : 'off'} onClick={() => openHome(k.customer_id)}>
                  <td><div className="who"><MiniBottle level={k.level ?? 0} w={20} /><div style={{ minWidth: 0 }}><b>{k.name}</b><span>{k.quartier}{k.level != null ? ` · ${k.level} %` : ''}</span></div></div></td>
                  <td><span className="mono" style={{ color: 'var(--ink)' }}>{k.sensor.serial}</span><div><Bat pct={k.sensor.battery_pct} /></div></td>
                  <td><span className="mono" style={{ color: 'var(--ink)' }}>{k.gateway.serial}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}><Sig rssi={k.gateway.rssi} online={k.online} /><span className="num muted">{!k.online ? S.hh.offline : k.gateway.rssi != null ? k.gateway.rssi + ' dBm' : '—'}</span></div></td>
                  <td>{k.received_at ? (k.online ? <span className="num">{rel(minutesBetween(k.received_at))}</span> : <span className="st red">{rel(minutesBetween(k.received_at))}</span>) : '—'}</td>
                  <td className="hm"><Strip hours={k.hours24} online={k.online} /></td>
                  <td>{k.gateway.fw ? <span className={'fw' + (fw && k.gateway.fw !== fw ? ' old' : '')}>{k.gateway.fw}</span> : '—'}</td>
                  <td><Ic n="chev" s="s" /></td>
                </tr>
              ))}
            </tbody></table>
          ) : <div className="empty"><b>{S.dv.noKits}</b>{S.dv.noKitsSub}</div>}
        </div>
      </div>
    </section>
  );
}
